import {
  BookMinusIcon,
  ChevronRightIcon,
  FileIcon,
  FolderIcon,
  SettingsIcon,
  Trash2Icon,
} from 'lucide-react';
import { type JSX, type ReactNode, useEffect, useState } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { cn } from '@/lib/utils.js';
import { meaningfulPath } from '@/features/sharepoint/row-notes.js';
import type {
  OneDriveAccount,
  OneDriveAutomationAccess,
  OneDrivePermissionLevel,
  OneDriveScope,
  OneDriveScopeStatus,
} from '@/lib/api/onedrive.js';

import {
  oneDriveErrorPresentation,
  type OneDriveErrorPresentation,
} from './onedrive-error.js';
import {
  KNOWLEDGE_OFF_CONFIRM,
  KNOWLEDGE_OFF_WARNING,
} from '@/features/microsoft/knowledge-off.js';

function scopeErrorPresentation(
  reason: unknown,
  fallback: string,
): OneDriveErrorPresentation {
  const presentation = oneDriveErrorPresentation(reason);
  return reason instanceof Error &&
    presentation.message === 'OneDrive is unavailable.'
    ? { ...presentation, message: reason.message || fallback }
    : presentation;
}

const STATUS_LABELS: Record<OneDriveScopeStatus, string> = {
  active: 'Active',
  folder_missing: 'Folder missing',
  account_unavailable: 'Account unavailable',
  scope_conflict: 'Folder conflict',
  access_revoked: 'Access revoked',
};

/** What one row is called in its own controls: a folder, or the single file a scope may be. */
function scopeNoun(scope: OneDriveScope): 'file' | 'folder' {
  return scope.isFile ? 'file' : 'folder';
}

/** The status badge, in the row's own noun — a file that is gone is not a missing folder. */
function statusLabel(scope: OneDriveScope): string {
  if (scope.isFile && scope.status === 'folder_missing') return 'File missing';
  if (scope.isFile && scope.status === 'scope_conflict') return 'File conflict';
  return STATUS_LABELS[scope.status] ?? 'Access revoked';
}

function accessLabel(access: OneDriveAutomationAccess): string {
  if (access === 'read_write') return 'Read and write';
  if (access === 'read') return 'Read';
  return 'Off';
}

/**
 * The clickable half of a row, or the same content as plain text where there is nowhere to click to.
 * One element either way, so the row's layout does not depend on whether the surface can browse.
 */
function RowBody({
  openable,
  current,
  onOpen,
  children,
}: {
  openable: boolean;
  current: boolean;
  onOpen: () => void;
  children: ReactNode;
}): JSX.Element {
  const className =
    'flex min-w-0 flex-1 items-start gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20';
  if (!openable) return <div className={className}>{children}</div>;
  return (
    <button
      type="button"
      aria-current={current ? 'true' : undefined}
      className={className}
      onClick={onOpen}
    >
      {children}
    </button>
  );
}

/**
 * The caller's connected OneDrive folders, with what can be done to each of them.
 *
 * One of those actions curates the workspace's knowledge base, and it is OPTIONAL on purpose: this list
 * is rendered on two surfaces, and only one of them — the Artifacts section, which knows the caller's
 * workspace role — has any business offering it. The composer's attachment dialog renders the same list
 * to attach a file to a message, and passing nothing is what keeps a third way into the workspace's
 * knowledge from appearing there. There is no second source of truth about the role here.
 */
/**
 * What a granted folder is called on its row.
 *
 * The whole drive is the exception and has to be: Graph names a drive's root `root` on some
 * tenants and `OneDrive` on others, so a row built from the snapshot read "root" — a word nobody
 * chose and nobody recognises. What the row IS is on the scope, so the name comes from that.
 */
function scopeTitle(scope: OneDriveScope): string {
  if (scope.isDriveRoot) return 'The entire OneDrive';
  return scope.alias ?? scope.originalName;
}

export function OneDriveScopeList({
  account,
  scopes,
  selectedScopeId,
  onSelect,
  onUpdate,
  onDelete,
  knowledge,
  searchable,
  compact = false,
  openByDesign = false,
}: {
  account: OneDriveAccount;
  scopes: readonly OneDriveScope[];
  selectedScopeId: string | null;
  /**
   * Opens the folder. Left out by a surface with nowhere to open it into — the folder-selection
   * dialog manages the list without browsing it — and the row's name is then plain text rather than a
   * control that looks clickable and does nothing.
   */
  onSelect?: (scopeId: string) => void;
  onUpdate: (
    scopeId: string,
    patch: {
      alias: string | null;
      interactiveAccess: OneDrivePermissionLevel;
      automationAccess: OneDriveAutomationAccess;
    },
  ) => Promise<unknown>;
  onDelete: (scopeId: string) => Promise<unknown>;
  /**
   * Where the folder stands on the workspace's knowledge shelf, and the way back off it. A row states
   * the standing either way and offers the removal only to a folder that is on the shelf. Left out
   * entirely by a surface whose caller may not curate the knowledge base, or that is not a place to do
   * it from — one prop, so a row never states a standing it cannot act on.
   *
   * There is deliberately no way ON from here. Registering a folder carries a warning that the
   * workspace index is shared, and that consent belongs to the knowledge picker, which asks it.
   *
   * Removing asks Graph for nothing — it is a registry delete — so it stays on the row through a
   * revoked grant, a deleted folder or an account waiting for consent. Gating it on health stranded an
   * indexed source at the exact moment somebody would want it gone.
   */
  /**
   * Whether the row is standing in a narrow column, where a labelled button costs more than it
   * says. Measured: beside the browser at a laptop width the actions took 130px of a 285px row and
   * left the name 89px to wrap in, which tripled the row's height. The label goes; the
   * `aria-label` does not, so nothing is lost to a screen reader.
   */
  compact?: boolean;
  /**
   * Whether everything is reachable because this is the owner's own personal workspace. A folder's
   * access levels then decide nothing, so they are not shown, and the list is the knowledge base.
   */
  openByDesign?: boolean;
  /**
   * Whether the contents are in the workspace's search index, and how to change it — the one setting
   * that used to live as a mark on the row. `of` answers null where the question does not apply.
   *
   * Left out entirely by a surface that has no business offering it, the same way `knowledge` is: one
   * prop, so a dialog never shows a control it cannot honour.
   */
  searchable?: {
    of: (row: OneDriveScope) => boolean | null;
    set: (row: OneDriveScope, next: boolean) => Promise<void>;
    /**
     * How the indexing is going, in the same words the Knowledge tab uses. Null where the row is
     * not on the shelf, or where the indexer has not answered — a switch that is on and says
     * nothing is the state somebody opened this to check.
     */
    statusOf?: (row: OneDriveScope) => string | null;
  };
  knowledge?: {
    /** Whether this folder's own root is a knowledge source of the workspace already. */
    isRegistered: (scope: OneDriveScope) => boolean;
    onRemove: (scope: OneDriveScope) => void;
  };
}): JSX.Element {
  const [editing, setEditing] = useState<OneDriveScope | null>(null);
  const [removing, setRemoving] = useState<OneDriveScope | null>(null);
  const [alias, setAlias] = useState('');
  const [interactiveAccess, setInteractiveAccess] =
    useState<OneDrivePermissionLevel>('read');
  const [automationAccess, setAutomationAccess] =
    useState<OneDriveAutomationAccess>('none');
  const [error, setError] = useState<OneDriveErrorPresentation | null>(null);
  const [busy, setBusy] = useState(false);
  /** Null while the question does not apply to the open row, which is also when no switch shows. */
  const [indexed, setIndexed] = useState<boolean | null>(null);
  /** Set once Save was pressed with the index switched off, so the next press is the one that removes. */
  const [confirmingOff, setConfirmingOff] = useState(false);
  /**
   * Read live rather than kept in state: the pass moves while this dialog is open, and a copy taken
   * when it opened would still say "Indexing…" long after it finished.
   */
  const indexStatus =
    editing && indexed === true
      ? (searchable?.statusOf?.(editing) ?? null)
      : null;

  useEffect(() => {
    if (!editing) return;
    setAlias(editing.alias ?? '');
    setInteractiveAccess(editing.interactiveAccess);
    setAutomationAccess(editing.automationAccess);
    setIndexed(searchable ? searchable.of(editing) : null);
    setConfirmingOff(false);
    setError(null);
  }, [editing]);

  async function save(): Promise<void> {
    if (!editing) return;
    const turningOff =
      searchable !== undefined && indexed === false && searchable.of(editing);
    if (turningOff && !confirmingOff) {
      setConfirmingOff(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onUpdate(editing.scopeId, {
        alias: alias.trim() || null,
        interactiveAccess,
        automationAccess,
      });
      /**
       * Indexing is a separate registry, so it is a separate call — and it runs only when the
       * switch actually moved, so a save that changed a name does not re-register anything.
       */
      if (
        searchable &&
        indexed !== null &&
        indexed !== searchable.of(editing)
      ) {
        await searchable.set(editing, indexed);
      }
      setEditing(null);
    } catch (reason) {
      setError(scopeErrorPresentation(reason, 'Could not save settings.'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    if (!removing) return;
    setBusy(true);
    setError(null);
    try {
      await onDelete(removing.scopeId);
      setRemoving(null);
    } catch (reason) {
      setError(scopeErrorPresentation(reason, 'Could not remove the folder.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    /*
      Compact fills its share of the dialog and scrolls inside itself, so the panel beside it ends
      at the same line — the two sides used to stop at different heights.
    */
    <div
      role="list"
      className={cn(
        'flex flex-col gap-1 rounded-xl border border-border p-2',
        compact && 'min-h-0 flex-1 overflow-y-auto',
      )}
    >
      {scopes.map((scope) => {
        const path = meaningfulPath(scope.parentPath);
        return (
          <div
            key={scope.scopeId}
            role="listitem"
            className={`flex items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors hover:bg-muted ${selectedScopeId === scope.scopeId ? 'bg-primary/10' : ''}`}
          >
            <RowBody
              openable={onSelect !== undefined}
              current={selectedScopeId === scope.scopeId}
              onOpen={() => onSelect?.(scope.scopeId)}
            >
              {scope.isFile ? (
                <FileIcon
                  className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                  aria-hidden
                />
              ) : (
                <FolderIcon
                  className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                  aria-hidden
                />
              )}
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-start justify-between gap-2">
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">
                      {scopeTitle(scope)}
                    </span>
                    {/*
                    Only when an alias hides the OneDrive name. Without one the title already is
                    that name, so this repeated it verbatim on almost every row. The whole drive is
                    never worth a second line either: `root` is Graph's word, not a name.
                  */}
                    {!scope.isDriveRoot &&
                      scope.alias !== null &&
                      scope.alias !== scope.originalName && (
                        <span className="block truncate text-xs text-muted-foreground">
                          OneDrive name: {scope.originalName}
                        </span>
                      )}
                    {path !== null && (
                      <span className="block truncate text-xs text-muted-foreground">
                        {compact ? path : `Path: ${path}`}
                      </span>
                    )}
                  </span>
                  {scope.status !== 'active' && (
                    <Badge variant="destructive">{statusLabel(scope)}</Badge>
                  )}
                </span>
                {/*
                  A compact row says WHAT it is and nothing about how it is set. Everything an add
                  grants is the same on every row, so the settings read as noise; the one row in
                  twenty that was narrowed states it where it is changed, behind the gear.
                */}
                {openByDesign && searchable?.of(scope) === false && (
                  <span className="block text-xs text-muted-foreground">
                    Not in knowledge
                  </span>
                )}
                {!compact && (
                  <span className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    {!openByDesign && (
                      <>
                        <span>
                          Interactive: {accessLabel(scope.interactiveAccess)}
                        </span>
                        <span>
                          Automation: {accessLabel(scope.automationAccess)}
                        </span>
                      </>
                    )}
                    {knowledge && (
                      <span>
                        Index: {knowledge.isRegistered(scope) ? 'on' : 'off'}
                      </span>
                    )}
                  </span>
                )}
              </span>
              {onSelect && (
                <ChevronRightIcon
                  className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                  aria-hidden
                />
              )}
            </RowBody>
            <div className="flex shrink-0 items-center gap-0.5">
              {!compact && knowledge && knowledge.isRegistered(scope) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => knowledge.onRemove(scope)}
                >
                  <BookMinusIcon /> Remove from knowledge
                </Button>
              )}
              {/*
              Absent for a non-owner rather than disabled, the same way the SharePoint place row
              treats its own controls: a member reads a workspace connection's folders, the owner
              decides what it exposes, and a greyed-out button reads as a fault to fix.
            */}
              {scope.canManage && (
                <>
                  <Button
                    variant="ghost"
                    size={compact ? 'icon-sm' : 'sm'}
                    aria-label={
                      scope.isFile ? 'File settings' : 'Folder settings'
                    }
                    disabled={account.status !== 'active'}
                    onClick={() => setEditing(scope)}
                  >
                    <SettingsIcon />
                    {!compact && 'Settings'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Remove ${scopeNoun(scope)}`}
                    onClick={() => {
                      setError(null);
                      setRemoving(scope);
                    }}
                  >
                    <Trash2Icon />
                  </Button>
                </>
              )}
            </div>
          </div>
        );
      })}

      <AppDialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        title={`OneDrive ${editing ? scopeNoun(editing) : 'folder'} settings`}
        description={
          openByDesign
            ? 'Change the display name and whether it is in the knowledge base.'
            : 'Change the display name and the two independent access levels.'
        }
      >
        <div className="flex flex-col gap-4 pt-3">
          <label className="flex flex-col gap-1 text-sm">
            {editing?.isFile ? 'File alias' : 'Folder alias'}
            <Input
              value={alias}
              onChange={(event) => setAlias(event.target.value)}
            />
          </label>
          {!openByDesign && (
            <>
              <label className="flex flex-col gap-1 text-sm">
                Interactive access
                <select
                  className="h-10 rounded-md border border-input bg-background px-3"
                  value={interactiveAccess}
                  onChange={(event) =>
                    setInteractiveAccess(
                      event.target.value as OneDrivePermissionLevel,
                    )
                  }
                >
                  <option value="read">Read</option>
                  <option value="read_write">Read and write</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Automation access
                <select
                  className="h-10 rounded-md border border-input bg-background px-3"
                  value={automationAccess}
                  onChange={(event) =>
                    setAutomationAccess(
                      event.target.value as OneDriveAutomationAccess,
                    )
                  }
                >
                  <option value="none">Off</option>
                  <option value="read">Read</option>
                  <option value="read_write">Read and write</option>
                </select>
              </label>
            </>
          )}
          {indexed !== null && (
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={indexed}
                onChange={(event) => {
                  setIndexed(event.target.checked);
                  setConfirmingOff(false);
                }}
              />
              <span className="flex flex-col gap-0.5">
                In the workspace knowledge base
                {/* One line, and the state beside it — see the SharePoint half. */}
                <span className="text-xs text-muted-foreground">
                  The assistant can quote it without being pointed at OneDrive.
                </span>
                {indexStatus && (
                  <span className="text-xs text-muted-foreground">
                    {indexStatus}
                  </span>
                )}
              </span>
            </label>
          )}
          {error && (
            <div className="text-sm">
              <p className="text-destructive">{error.message}</p>
              <p className="text-muted-foreground">{error.remediation}</p>
            </div>
          )}
          {confirmingOff && (
            <p className="text-sm text-destructive">{KNOWLEDGE_OFF_WARNING}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button disabled={busy} onClick={() => void save()}>
              {confirmingOff ? KNOWLEDGE_OFF_CONFIRM : 'Save settings'}
            </Button>
          </div>
        </div>
      </AppDialog>

      <AppDialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
        title={`Remove OneDrive ${removing ? scopeNoun(removing) : 'folder'}`}
        description={
          openByDesign
            ? `This takes the ${removing ? scopeNoun(removing) : 'folder'} off the list and out of the knowledge base. The agent still reaches it through the whole drive. Existing live references to it stop working. Files remain in OneDrive.`
            : `This revokes the ${removing ? scopeNoun(removing) : 'folder'} from this workspace. Existing live references lose access immediately. Files remain in OneDrive.`
        }
      >
        <div className="flex flex-col gap-4 pt-3">
          <p className="text-sm text-muted-foreground">
            Files remain in OneDrive. The{' '}
            {removing ? scopeNoun(removing) : 'folder'} also leaves this
            workspace&apos;s knowledge base.
          </p>
          {error && (
            <div className="text-sm">
              <p className="text-destructive">{error.message}</p>
              <p className="text-muted-foreground">{error.remediation}</p>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() => void remove()}
            >
              {`Remove ${removing ? scopeNoun(removing) : 'folder'}`}
            </Button>
          </div>
        </div>
      </AppDialog>
    </div>
  );
}
